import { useEffect, useState } from 'react';
import axios from 'axios';
import { EyeIcon, UsersIcon } from 'lucide-react';
import {
  Button,
  DataTable,
  EmptyState,
  PageHeading,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  SkeletonTable,
  useDebouncedValue,
} from '@bcecloud/portal-ui';
import { getToken } from '@/lib/auth';
import { setViewAsCustomer } from '@/lib/view-as';

/** Rows per page. */
const PAGE_SIZE = 25;

/** One event × one person who can open it, as the API returns it. */
interface PortalAccessRow {
  readonly eventId: string;
  readonly contractNumber: string | null;
  readonly programName: string | null;
  readonly accountName: string | null;
  readonly eventDate: string | null;
  readonly timeStartString: string | null;
  readonly timeZone: string | null;
  readonly city: string | null;
  readonly state: string | null;
  readonly person: {
    readonly email: string;
    readonly name: string | null;
    readonly userId: string | null;
    readonly accountRole: string | null;
  };
}

interface PortalAccessResponse {
  readonly data: PortalAccessRow[];
  readonly pagination: { readonly page: number; readonly total: number; readonly totalPages: number };
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * The staff way in: every event a customer can open, fanned out by the
 * people who can open it, searchable by program, account, contract number
 * or person. "View as" opens My Events read-only as that customer — by their
 * account when they have one, by their address when they have not signed in
 * yet, which shows exactly what they will see when they do.
 *
 * @returns The picker.
 */
export function CustomerPickerPage(): React.ReactElement {
  const [state, setState] = useState<'upcoming' | 'past'>('upcoming');
  const [search, setSearch] = useState('');
  const term = useDebouncedValue(search.trim(), 250);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<PortalAccessRow[]>([]);
  const [pagination, setPagination] = useState<PortalAccessResponse['pagination'] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPage(1);
  }, [state, term]);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      try {
        const { data } = await axios.get<PortalAccessResponse>('/api/v3/events/portal-access', {
          params: { state, page, limit: PAGE_SIZE, ...(term ? { search: term } : {}) },
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (cancelled) return;
        setRows(data.data);
        setPagination(data.pagination);
      } catch {
        if (!cancelled) {
          setRows([]);
          setPagination(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [state, term, page]);

  /** Starts viewing as this person and opens their event. A full load, so the provider mounts for the new target. */
  function viewAs(row: PortalAccessRow): void {
    setViewAsCustomer({
      id: row.person.userId ?? row.person.email,
      name: row.person.name ?? row.person.email,
      email: row.person.email,
    });
    window.location.assign(`/events/${row.eventId}/overview`);
  }

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="BCE STAFF"
        title="Open My Events as a customer"
        subtitle="Every event a customer can open, by the people who can open it. Pick a person to see the portal exactly as they do, read-only."
        action={
          <SegmentedControl
            label="Show events"
            options={[
              { key: 'upcoming', label: 'Upcoming' },
              { key: 'past', label: 'Past' },
            ]}
            value={state}
            onChange={(key) => setState(key as 'upcoming' | 'past')}
          />
        }
      />

      <div className="mb-4 max-w-md">
        <SearchInput
          label="Search events and people"
          placeholder="Program, account, contract number, name or email"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {loading ? (
        <SkeletonTable rows={8} columns={['w-[30%]', 'w-[16%]', 'w-[30%]', 'w-[12%]']} label="Loading customers…" />
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<UsersIcon aria-hidden className="size-6" />}
            title={term ? 'Nobody matches that' : 'No customers to show'}
            message={
              term
                ? 'Try a program name, an account, a contract number, or part of a name or email.'
                : state === 'upcoming'
                  ? 'Upcoming events with a client address on them appear here as they are booked.'
                  : 'No past events with a client address on them.'
            }
          />
        </Panel>
      ) : (
        <>
          <DataTable headers={['Event', 'Date', 'Customer', 'Access', '']} minWidth={860}>
            {rows.map((row) => (
              <tr
                key={`${row.eventId}:${row.person.email}`}
                className="border-t border-portal-line align-middle text-portal-tiny"
              >
                <td className="px-4 py-3">
                  <span className="block text-portal-small font-bold">{row.programName ?? '—'}</span>
                  <span className="block text-portal-micro text-portal-muted">
                    {[row.accountName, row.contractNumber, [row.city, row.state].filter(Boolean).join(', ')]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="block">{formatDate(row.eventDate)}</span>
                  <span className="block text-portal-micro text-portal-muted">
                    {[row.timeStartString, row.timeZone].filter(Boolean).join(' ')}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="block text-portal-small font-bold">{row.person.name ?? row.person.email}</span>
                  {row.person.name ? (
                    <span className="block text-portal-micro text-portal-muted">{row.person.email}</span>
                  ) : null}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {row.person.userId ? (
                    <Pill tone="success">Has an account</Pill>
                  ) : row.person.accountRole ? (
                    <Pill tone="neutral">{row.person.accountRole} account</Pill>
                  ) : (
                    <Pill tone="neutral">Not signed in yet</Pill>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {row.person.accountRole ? (
                    <span className="text-portal-micro text-portal-muted">No customer portal</span>
                  ) : (
                    <Button variant="secondary" size="compact" onClick={() => viewAs(row)}>
                      <EyeIcon aria-hidden className="size-4" />
                      {row.person.userId ? 'View as' : 'Preview'}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </DataTable>

          {pagination && pagination.totalPages > 1 ? (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              summary={`${pagination.total} people across events`}
              onChange={setPage}
            />
          ) : null}
        </>
      )}
    </section>
  );
}
